import {
  IVaultMeeting,
  IVaultMeetingFilters,
  IVaultTranscriptChoice,
} from "./VaultMeetingsDialog.types";

export const DAYS_PER_WEEK = 7;
export const HOURS_PER_HALF_DAY = 12;
export const NOON_HOUR = 12;
export const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export const DAY_NAMES_SHORT = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];
export const DAY_NAMES_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const EMPTY_VAULT_MEETING_FILTERS: IVaultMeetingFilters = {
  titleText: "",
  transcriptStatus: "any",
};

export const VAULT_TRANSCRIPT_CHOICES: IVaultTranscriptChoice[] = [
  { value: "any", label: "Any transcript status" },
  { value: "with", label: "With transcript" },
  { value: "without", label: "No transcript" },
];

const STANDUP_TITLE = "NH AIMS Platform Daily Standup";

interface IFixtureSeed {
  id: string;
  dayOffset: number;
  hour: number;
  minute: number;
  durationMinutes: number;
  title: string;
  hasTranscript: boolean;
  isSaved?: boolean;
}

// Day offsets are counted from the Sunday that starts the reference week, so
// 1 is Monday of that week and -6 is Monday of the week before.
const FIXTURE_SEEDS: IFixtureSeed[] = [
  {
    id: "m01",
    dayOffset: -13,
    hour: 14,
    minute: 0,
    durationMinutes: 25,
    title: STANDUP_TITLE,
    hasTranscript: true,
    isSaved: true,
  },
  {
    id: "m02",
    dayOffset: -12,
    hour: 15,
    minute: 0,
    durationMinutes: 60,
    title: "Sprint Planning",
    hasTranscript: true,
  },
  {
    id: "m03",
    dayOffset: -6,
    hour: 14,
    minute: 0,
    durationMinutes: 25,
    title: STANDUP_TITLE,
    hasTranscript: true,
    isSaved: true,
  },
  {
    id: "m04",
    dayOffset: -5,
    hour: 10,
    minute: 0,
    durationMinutes: 45,
    title: "Design Review",
    hasTranscript: false,
  },
  {
    id: "m05",
    dayOffset: -4,
    hour: 16,
    minute: 0,
    durationMinutes: 30,
    title: "Retrospective",
    hasTranscript: true,
  },
  {
    id: "m06",
    dayOffset: 1,
    hour: 8,
    minute: 0,
    durationMinutes: 25,
    title: "Reminder: mid-period evaluation",
    hasTranscript: false,
  },
  {
    id: "m07",
    dayOffset: 1,
    hour: 14,
    minute: 0,
    durationMinutes: 25,
    title: STANDUP_TITLE,
    hasTranscript: true,
  },
  {
    id: "m08",
    dayOffset: 2,
    hour: 14,
    minute: 0,
    durationMinutes: 25,
    title: STANDUP_TITLE,
    hasTranscript: true,
  },
  {
    id: "m09",
    dayOffset: 2,
    hour: 14,
    minute: 30,
    durationMinutes: 25,
    title: "Alex/Jordan 1:1",
    hasTranscript: false,
  },
  {
    id: "m10",
    dayOffset: 3,
    hour: 14,
    minute: 0,
    durationMinutes: 25,
    title: STANDUP_TITLE,
    hasTranscript: true,
  },
  {
    id: "m11",
    dayOffset: 3,
    hour: 15,
    minute: 0,
    durationMinutes: 75,
    title: "NH360 - Q3 Quarterly Meeting",
    hasTranscript: true,
    isSaved: true,
  },
  {
    id: "m12",
    dayOffset: 3,
    hour: 16,
    minute: 0,
    durationMinutes: 30,
    title: "AI in Action Community Call",
    hasTranscript: false,
  },
  {
    id: "m13",
    dayOffset: 3,
    hour: 17,
    minute: 0,
    durationMinutes: 30,
    title: "Monthly Workforce Transformation Fireside Chats",
    hasTranscript: false,
  },
  {
    id: "m14",
    dayOffset: 4,
    hour: 14,
    minute: 0,
    durationMinutes: 25,
    title: STANDUP_TITLE,
    hasTranscript: true,
  },
  {
    id: "m15",
    dayOffset: 4,
    hour: 16,
    minute: 0,
    durationMinutes: 25,
    title: "Change Control",
    hasTranscript: true,
  },
  {
    id: "m16",
    dayOffset: 4,
    hour: 18,
    minute: 0,
    durationMinutes: 60,
    title: "Brave Space: Honouring Our Communities",
    hasTranscript: false,
  },
  {
    id: "m17",
    dayOffset: 5,
    hour: 14,
    minute: 0,
    durationMinutes: 25,
    title: STANDUP_TITLE,
    hasTranscript: true,
  },
  {
    id: "m18",
    dayOffset: 5,
    hour: 18,
    minute: 0,
    durationMinutes: 25,
    title: "End-of-period evaluation close-out",
    hasTranscript: false,
  },
];

const getSundayStart = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());

/**
 * Builds the dummy meetings relative to the week that contains `referenceDate`
 * so the default view is never empty. Includes meetings in earlier weeks.
 */
export const createVaultMeetingFixtures = (
  referenceDate: Date,
): IVaultMeeting[] => {
  const weekStart = getSundayStart(referenceDate);
  return FIXTURE_SEEDS.map(
    ({
      id,
      dayOffset,
      hour,
      minute,
      durationMinutes,
      title,
      hasTranscript,
      isSaved,
    }) => {
      const start = new Date(
        weekStart.getFullYear(),
        weekStart.getMonth(),
        weekStart.getDate() + dayOffset,
        hour,
        minute,
      );
      const end = new Date(start.getTime());
      end.setMinutes(end.getMinutes() + durationMinutes);
      return {
        id,
        title,
        start,
        end,
        hasTranscript,
        isSaved: Boolean(isSaved),
      };
    },
  );
};
